import * as React from 'react'

import { reaction } from 'mobx'
import { observer } from 'mobx-react'

import ComposerSatellites from './ComposerSatellites'
import WorkflowComposerStore from './WorkflowComposer.store'
import WorkflowComposerCard from './WorkflowComposerCard'

interface IProps {
  store: WorkflowComposerStore
}

// The card remounts across expand ⇄ minimize, so anchoring is (un)bound per mount while
// the store itself outlives it (owned by the session overlay). When minimized, nothing is
// pin-anchored — the form is represented by the "Open workflow" button docked in the
// markup toolbar row (rendered by the overlay via dockedTrailing).
const DesktopWorkflowComposer: React.FC<IProps> = observer(({ store }) => {
  const { anchorController } = store
  React.useLayoutEffect(() => {
    anchorController.startAnchoring()
    return () => {
      anchorController.stopAnchoring()
      store.clearDockedToolbar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Engaging a markup tool auto-collapses the form to the Dock (only from 'expanded');
  // finishing (editor back to idle) re-expands it — but only if WE auto-collapsed. A
  // deliberate minimize (Minimize/Esc → continuous-drawing) stays in the Dock until the
  // user hits Open workflow. fireImmediately covers draw-first entry (mounts engaged).
  React.useEffect(
    () =>
      reaction(
        () => store.isMarkupEngaged,
        engaged => {
          if (engaged) {
            if (store.snap === 'expanded') store.autoMinimize()
          } else if (store.autoMinimized) {
            store.maximize()
          }
        },
        { fireImmediately: true },
      ),
    [store],
  )

  // Re-anchor AFTER the card ⇄ pill swap commits: setSnap's synchronous reproject
  // measures the old DOM (stale toolbar height), and a tool engaging while already
  // collapsed fires no setSnap at all.
  React.useLayoutEffect(() => {
    anchorController.reproject()
  }, [anchorController, store.isMinimized, store.isMarkupEngaged])

  return (
    <>
      {/* The controller writes the position per camera frame (never React); React
          only unhides it once the first projection lands. */}
      <div
        ref={anchorController.wrapperRef}
        className="tw:pointer-events-auto tw:absolute tw:top-0 tw:left-0 tw:z-30"
        style={
          anchorController.hasPosition ? undefined : { visibility: 'hidden' }
        }
      >
        {!store.isMinimized && <WorkflowComposerCard store={store} />}
      </div>
      <ComposerSatellites
        store={store.formStore}
        anchorRect={store.pickerAnchor}
        allowedTypeIds={store.offerableWorkflowTypeIds}
      />
    </>
  )
})

export default DesktopWorkflowComposer
